import { fetchAuthSession } from 'aws-amplify/auth';
import { getApiUrl } from '../auth/config';

export interface WeightEntry {
  userId: string;
  recordedAt: string;
  weightLbs: number;
}

interface WeightsCache {
  etag?: string;
  since: string;
  items: WeightEntry[];
}

const WEIGHTS_CACHE_KEY = 'weights_etag_cache_v1';
let weightsCache: WeightsCache | null = null;

function apiBase(): string {
  const url = getApiUrl();
  if (!url) throw new Error('API URL is not configured. Check VITE_API_URL in .env.local or apiUrl in config.json.');
  return url;
}

async function authHeader(): Promise<string> {
  const session = await fetchAuthSession();
  const token = session.tokens?.idToken?.toString();
  if (!token) throw new Error('Not authenticated');
  return `Bearer ${token}`;
}

function loadCache(): WeightsCache | null {
  if (weightsCache) return weightsCache;
  if (typeof window === 'undefined') return null;

  const raw = window.sessionStorage.getItem(WEIGHTS_CACHE_KEY);
  if (!raw) return null;

  try {
    const parsed = JSON.parse(raw) as Partial<WeightsCache>;
    if (!parsed.since || !Array.isArray(parsed.items)) return null;
    weightsCache = {
      since: parsed.since,
      etag: parsed.etag,
      items: parsed.items as WeightEntry[],
    };
    return weightsCache;
  } catch {
    return null;
  }
}

function saveCache(cache: WeightsCache): void {
  weightsCache = cache;
  if (typeof window !== 'undefined') {
    window.sessionStorage.setItem(WEIGHTS_CACHE_KEY, JSON.stringify(cache));
  }
}

function clearCache(): void {
  weightsCache = null;
  if (typeof window !== 'undefined') {
    window.sessionStorage.removeItem(WEIGHTS_CACHE_KEY);
  }
}

export async function getWeights(): Promise<WeightEntry[]> {
  const cache = loadCache();
  const headers: Record<string, string> = { Authorization: await authHeader() };
  if (cache?.etag && !cache.since) {
    headers['If-None-Match'] = cache.etag;
  }
  const path = cache?.since
    ? `${apiBase()}/weights?since=${encodeURIComponent(cache.since)}`
    : `${apiBase()}/weights`;

  const res = await fetch(path, {
    headers,
  });

  if (res.status === 304) {
    if (!cache) throw new Error('Weights cache miss after 304');
    return cache.items;
  }

  if (!res.ok) throw new Error(`Failed to fetch weights: ${res.status}`);

  const data = (await res.json()) as {
    items: WeightEntry[];
    deleted?: Array<{ recordedAt: string }>;
    nextSince?: string;
  };

  const etag = res.headers.get('etag');
  const nextSince = data.nextSince ?? new Date().toISOString();
  const deletedSet = new Set((data.deleted ?? []).map((d) => d.recordedAt));

  if (cache?.since) {
    const merged = new Map(cache.items.map((item) => [item.recordedAt, item]));
    for (const deleted of deletedSet) merged.delete(deleted);
    for (const item of data.items) merged.set(item.recordedAt, item);
    const items = Array.from(merged.values()).sort((a, b) => a.recordedAt.localeCompare(b.recordedAt));
    saveCache({ etag: etag ?? cache.etag, since: nextSince, items });
    return items;
  }

  if (etag || data.nextSince) {
    saveCache({
      etag: etag ?? undefined,
      since: nextSince,
      items: data.items,
    });
  } else {
    clearCache();
  }

  return data.items;
}

export async function logWeight(weightLbs: number, recordedAt: string): Promise<void> {
  const res = await fetch(`${apiBase()}/weights`, {
    method: 'POST',
    headers: {
      Authorization: await authHeader(),
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({ weightLbs, recordedAt }),
  });
  if (!res.ok) {
    const body = await res.text();
    throw new Error(`Failed to log weight: ${res.status} ${body}`);
  }
}

export async function deleteWeight(recordedAt: string): Promise<void> {
  const res = await fetch(`${apiBase()}/weights/${encodeURIComponent(recordedAt)}`, {
    method: 'DELETE',
    headers: { Authorization: await authHeader() },
  });
  if (!res.ok) {
    const body = await res.text();
    throw new Error(`Failed to delete entry: ${res.status} ${body}`);
  }
}

export async function updateWeight(
  oldRecordedAt: string,
  weightLbs: number,
  recordedAt: string,
): Promise<void> {
  const res = await fetch(`${apiBase()}/weights/${encodeURIComponent(oldRecordedAt)}`, {
    method: 'PUT',
    headers: {
      Authorization: await authHeader(),
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({ weightLbs, recordedAt }),
  });
  if (!res.ok) {
    const body = await res.text();
    throw new Error(`Failed to update entry: ${res.status} ${body}`);
  }
}
